import { FC, Fragment, useCallback, useMemo } from 'react';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useLocale } from '@app/core/hooks/useLocale';
import { useParams } from 'react-router-dom';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import CellValue from '@app/shared/components/cell-value/CellValue';
import styles from './ordinary-equity-add-redemption-section.module.scss';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import { Tooltip, TooltipPlacement } from '@app/shared/components/tooltip/Tooltip';
import { TooltipWidth } from '@app/shared/models/contracts/enums/shared-enums';
import Button from '@app/shared/components/button/Button';
import SvgInformation from '@app/shared/icons/Information';
import { Alignment } from '@app/shared/enums/alignment.enum';
import DealThesisEmptyHistoricDataColumns from '../../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import { Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto } from '@app/shared/models/contracts/pwerm2-input-calculation-results-dto';
import { dateFormatter } from '@app/shared/formatters';
import {
  InstrumentDefinitionWithId,
  selectOrdinaryEquityInstruments,
} from '@app/core/store/capital-structure-slice-selectors';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import useDealThesisData from '../../deal-thesis-utils/useDealThesisData';

const OrdinaryEquityAdditionRedemptionSection: FC = (): JSX.Element => {
  const { l } = useLocale();
  const { caseId } = useParams();
  const ordinaryEquityInstruments = useAppSelector(selectOrdinaryEquityInstruments);
  const sortedOrdinaryEquityInstruments = useInstrumentSort(ordinaryEquityInstruments);
  const buildStructures = useAppSelector((state) => state.capitalStructure.values.buildStructures);

  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);

  // TO BE REPLACED ON HOW ITS BEING DONE ON OPM CASE SELECTOR
  const capitalStructureKey = Object.keys(buildStructures)?.[0];
  const isErfProject = buildStructures?.[capitalStructureKey].isErf;

  const caseResults = results.cases[caseId!];

  const {
    getMaxColspan,
    caseResultsForecastDates,
    filteredHistoricEventData,
    showHistoricDataColumns,
  } = useDealThesisData(caseId);

  const hasInstrumentActivity = (
    instrumentData: Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto | undefined | null
  ) => Boolean(instrumentData?.periodTotal?.additions);

  const checkInstrumentPresence = useCallback(
    (instrument: InstrumentDefinitionWithId) => {
      const isInstrumentPresentAcrossYears = Object.values(caseResultsForecastDates).some((f) =>
        hasInstrumentActivity(f.ordinaryInstruments[instrument.instrumentId])
      );

      const isInstrumentPresentInHistoricEvents = filteredHistoricEventData.some(
        (historicEventYear) =>
          hasInstrumentActivity(historicEventYear.ordinaryInstruments[instrument.instrumentId])
      );

      const isInstrumentFullyRedeemed = Object.values(caseResultsForecastDates).some(
        (forecast) => !forecast.ordinaryInstruments?.[instrument.instrumentId]
      );

      const valuationDateOrdinaryInstrument =
        caseResults.valuationDate.ordinaryInstruments?.[instrument.instrumentId];

      const isInstrumentPresentBeforeValuationDate = hasInstrumentActivity(
        valuationDateOrdinaryInstrument
      );

      return {
        isInstrumentPresentAcrossYears,
        isInstrumentPresentInHistoricEvents,
        isInstrumentFullyRedeemed,
        isInstrumentPresentBeforeValuationDate,
      };
    },
    [caseResultsForecastDates, filteredHistoricEventData, caseResults]
  );

  const instrumentsWithMovements = useMemo(
    () =>
      sortedOrdinaryEquityInstruments.map((instrument) => ({
        instrument,
        presence: checkInstrumentPresence(instrument),
      })),
    [sortedOrdinaryEquityInstruments, checkInstrumentPresence]
  );

  if (!isErfProject) {
    return <></>;
  }

  const anyMovementPresent = instrumentsWithMovements.some(
    (i) =>
      i.presence.isInstrumentPresentAcrossYears ||
      i.presence.isInstrumentPresentInHistoricEvents ||
      i.presence.isInstrumentFullyRedeemed ||
      i.presence.isInstrumentPresentBeforeValuationDate
  );

  if (!anyMovementPresent) {
    return <></>;
  }

  const renderTooltipContent = (
    instruments: Dictionary<Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto>,
    instrumentId: string
  ) => (
    <>
      <table>
        <thead>
          <tr className="table-primary__row--plain">
            <th>
              <strong>{l('_EventDateTitle')}</strong>
            </th>
            <th>
              <strong>{l('_InfusionOrBuybackNegative')}</strong>
            </th>
          </tr>
        </thead>
        <tbody>
          {(instruments[instrumentId]?.periodByEvent || []).map((event) => {
            if (event.additions === 0) {
              return null;
            }
            return (
              <tr className="table-primary--no-__row--plain" key={`${instrumentId}-${event.date}`}>
                <td>{dateFormatter(event.date.toString(), { dateStyle: 'short' })}</td>
                <td>
                  <CellValue alignment={Alignment.Right} {...amountRules} value={event.additions} />
                </td>
              </tr>
            );
          })}
          <tr>
            <th className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
              <strong>{l('_Total')}</strong>
            </th>
            <td className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
              <CellValue
                alignment={Alignment.Right}
                {...amountRules}
                value={instruments[instrumentId]?.periodTotal?.additions}
                strong
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );

  const renderTableRowsCell = (
    instruments: Dictionary<Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto>,
    instrumentId: string,
    index?: number
  ) => {
    const needsTooltip =
      instruments[instrumentId]?.periodByEvent.filter((event) => event.additions !== 0).length > 1;

    return (
      <td key={index !== undefined ? `${instrumentId}-${index}` : 'oe-valuation-total'}>
        {instruments && instrumentId in instruments && (
          <>
            <div style={{ display: 'flex', justifyContent: 'end' }}>
              <CellValue
                {...amountRules}
                alignment={Alignment.Right}
                value={instruments[instrumentId]?.periodTotal?.additions}
              />
              {needsTooltip && (
                <Tooltip
                  placement={TooltipPlacement.Top}
                  width={`${TooltipWidth.OrdinaryEquityDealThesisSection}px`}
                  content={renderTooltipContent(instruments, instrumentId)}>
                  <Button
                    className={styles['tooltip-button']}
                    startIcon={<SvgInformation />}
                    appearance={ButtonAppearance.CLEAN}
                    size={ButtonSize.SMALL}
                  />
                </Tooltip>
              )}
            </div>
          </>
        )}
      </td>
    );
  };

  const calculateTotalAdditions = (
    instruments: Dictionary<Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto>
  ) => {
    return instruments
      ? Object.values(instruments).reduce((acc, value) => {
          if (value.periodTotal !== null && value.periodTotal.additions !== 0) {
            acc += value.periodTotal.additions;
          }
          return acc;
        }, 0)
      : null;
  };

  const renderTableTotalsCell = (totalAdditions: number | null, index?: number) => (
    <td
      className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
      key={index !== undefined ? `${index}` : 'oe-valuation-total'}>
      <CellValue {...amountRules} alignment={Alignment.Right} value={totalAdditions} strong />
    </td>
  );

  return (
    <tbody>
      <tr>
        <th className="table-primary__cell--header">{l('_OrdinaryEquityInstruments')}</th>
        <td className="table-primary__cell--header" colSpan={getMaxColspan() - 1} />
      </tr>
      <tr className="table-primary__row--header">
        <th className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
          <strong>{l('_InfusionOrBuybackNegative')}</strong>
        </th>
        <td
          className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator"
          colSpan={getMaxColspan() - 1}
        />
      </tr>
      {instrumentsWithMovements.map((instrumentWithMovement) => {
        const { instrument, presence } = instrumentWithMovement;

        if (
          !presence.isInstrumentPresentAcrossYears &&
          !presence.isInstrumentPresentInHistoricEvents &&
          !presence.isInstrumentFullyRedeemed &&
          !presence.isInstrumentPresentBeforeValuationDate
        ) {
          return <Fragment key={instrument.instrumentId} />;
        }

        return (
          <tr key={instrument.instrumentId}>
            <th className="table-primary__cell--vertical-separator">
              {instrument.instrumentNarrative}
            </th>
            <DealThesisEmptyHistoricDataColumns isHistoricEventsSection />
            {showHistoricDataColumns &&
              filteredHistoricEventData.map((historicEventYear, index) =>
                renderTableRowsCell(
                  historicEventYear.ordinaryInstruments,
                  instrument.instrumentId,
                  index
                )
              )}
            {renderTableRowsCell(
              results.cases[caseId!].valuationDate.ordinaryInstruments,
              instrument.instrumentId
            )}
            {caseResultsForecastDates.map((forecast, index) =>
              renderTableRowsCell(forecast.ordinaryInstruments, instrument.instrumentId, index)
            )}
            <td />
          </tr>
        );
      })}

      <tr className="table-primary__row--plain">
        <th className="table-primary__cell--vertical-separator table-primary__cell--section-start-separator table-primary__cell--section-end-separator">
          <strong>{l('_Total')}</strong>
        </th>
        <DealThesisEmptyHistoricDataColumns
          isHistoricEventsSection
          isStartSeparator
          isEndSeparator
        />
        {showHistoricDataColumns &&
          filteredHistoricEventData.map((historicEventYear, index) => {
            const totalAdditions = calculateTotalAdditions(historicEventYear.ordinaryInstruments);
            return renderTableTotalsCell(totalAdditions, index);
          })}
        {renderTableTotalsCell(
          calculateTotalAdditions(results.cases[caseId!].valuationDate.ordinaryInstruments)
        )}
        {caseResultsForecastDates.map((forecast, index) => {
          const totalAdditions = calculateTotalAdditions(forecast.ordinaryInstruments);
          return renderTableTotalsCell(totalAdditions, index);
        })}
        <td className="table-primary__cell--section-start-separator table-primary__cell--section-end-separator" />
      </tr>
    </tbody>
  );
};

export default OrdinaryEquityAdditionRedemptionSection;
